// Two-stage armed-delete button. First click arms (icon swaps to a
// pulsing AlertTriangle, bg tints danger); second click within the
// parent's arm window fires `onDelete`. The parent owns the armed
// state and the timeout that resets it — this component is just the
// render.
//
// Extracted 2026-04-24 per Reef audit — RailTree (project +
// channel) and MessageCard both used to inline the same icon swap +
// pulse classes. Sizes differed (12, 10, 5/4) so the component
// takes a `size` prop and scales the hit-target accordingly.

import { AlertTriangle, Trash2 } from "lucide-react";

export function ArmedDeleteButton({
  armed,
  onDelete,
  size = 12,
  label,
  testId,
  className = "",
}: {
  armed: boolean;
  onDelete: () => void;
  /** Icon size in px; the square hit-target grows with it. */
  size?: number;
  /** Used for title + aria-label — e.g. "Delete #general",
   * "Delete message", "Delete Test 5". */
  label: string;
  testId?: string;
  /** Extra classes the parent layers on for positioning (absolute,
   * right-*, top-*, etc.). */
  className?: string;
}) {
  // Hit target scales with the icon: a 12-px icon inside a ~20-px
  // square, a 10-px icon inside ~16-px, etc. Keeps padding
  // consistent without a prop explosion.
  const box = Math.max(size + 8, 16);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      className={`flex items-center justify-center rounded transition-all ${
        armed
          ? "animate-pulse bg-danger/20 text-danger opacity-100"
          : "text-faint opacity-0 hover:bg-surface hover:text-danger group-hover:opacity-100"
      } ${className}`}
      style={{ width: box, height: box }}
      title={armed ? "Click again to delete" : label}
      aria-label={armed ? `Confirm ${label.toLowerCase()}` : label}
      data-testid={testId}
    >
      {armed ? <AlertTriangle size={size} /> : <Trash2 size={size} />}
    </button>
  );
}
